import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import Sections from '~/constants/vdp/sections';

type ContextType = {
  sectionsMounted: Partial<Record<Sections, boolean>>;
  sectionsViewed: Partial<Record<Sections, boolean>>;
  setSectionMounted: (section: keyof ContextType['sectionsMounted']) => void;
  setSectionViewed: (section: keyof ContextType['sectionsViewed']) => void;
};

const defaultContext: ContextType = {
  sectionsMounted: {
    [Sections.Hero]: true,
    [Sections.Inspection]: false,
    [Sections.OwnerReviews]: false,
    [Sections.PriceDetails]: false,
    [Sections.RecommendedVehicles]: false,
    [Sections.Spinner]: true,
    [Sections.VehicleDetails]: false
  },
  sectionsViewed: {
    [Sections.Gallery]: false,
    [Sections.RecommendedVehicles]: false
  },
  setSectionMounted: () => {},
  setSectionViewed: () => {}
};

const UiContext = createContext<ContextType>(defaultContext);

export function UiContextProvider({ children }: React.PropsWithChildren<{}>) {
  const [sectionsViewed, setSectionsViewed] = useState(
    defaultContext.sectionsViewed
  );
  const [sectionsMounted, setSectionsMounted] = useState(
    defaultContext.sectionsMounted
  );

  const setSectionViewed = useCallback((section: Sections) => {
    setSectionsViewed(prev => ({ ...prev, [section]: true }));
  }, []);

  const setSectionMounted = useCallback((section: Sections) => {
    setSectionsMounted(prev => ({ ...prev, [section]: true }));
  }, []);

  const value = useMemo(
    () => ({
      sectionsViewed,
      setSectionViewed,
      sectionsMounted,
      setSectionMounted
    }),
    [sectionsViewed, setSectionViewed, sectionsMounted, setSectionMounted]
  );

  return <UiContext.Provider value={value}>{children}</UiContext.Provider>;
}

UiContextProvider.displayName = 'UiContextProvider';

export default UiContext;

export function useSectionViewed(section: Sections) {
  return useContext(UiContext).sectionsViewed[section] ?? false;
}

export function useSectionMounted(section: Sections) {
  return useContext(UiContext).sectionsMounted[section] ?? false;
}
